/**
 * Which Base UI `onOpenChange(false, { reason })` events count as a GENUINE user
 * dismissal of the ROOT menu — i.e., the only ones the `Menu` wrapper forwards
 * to the consumer's `onClose`.
 *
 * Why an allowlist: Base UI's floating tree emits non-dismissal close events on
 * the parent root during submenu lifecycle — `sibling-open` when a submenu
 * opens (GH #1474) and `trigger-hover` when it closes (observed live; the
 * denylist guard for `sibling-open` alone let it through, unmounting the whole
 * menu when a submenu closed). The reason vocabulary is 30+ entries and grows;
 * enumerating the handful of real dismissals is robust where excluding leaks
 * one-by-one is not.
 */
const menuDismissalReasons = [
  /** Clicked/tapped outside the menu. */
  'outside-press',
  /** Escape pressed with the root menu deepest-open. */
  'escape-key',
  /** A menu item was activated — menus close on activation. */
  'item-press',
  /** An explicit close affordance was pressed. */
  'close-press',
  /** The anchor/trigger was pressed again to toggle the menu shut. */
  'trigger-press',
] as const

export const isMenuDismissalReason = (reason: string, event?: Event): boolean => {
  /**
   * `focus-out` is genuine ONLY when focus actually moved to an element outside
   * every menu popup (Tab-away — menus close on Tab per the APG). During a
   * submenu's unmount, the root also receives `focus-out` while focus is being
   * juggled: `relatedTarget` is then null (focus dropped to body) or a node
   * inside another menu popup. Forwarding those tore the root down when a
   * submenu closed. Discriminate on the FocusEvent's relatedTarget.
   */
  if (reason === 'focus-out') {
    return isFocusLeavingMenuTree(event)
  }
  return (menuDismissalReasons as readonly string[]).includes(reason)
}

/**
 * True when a focus-out's relatedTarget shows focus genuinely leaving the menu
 * tree (Tab-away to a real element outside every menu popup). False when focus
 * dropped to body/null mid-transition or moved within the menu tree — those
 * are submenu-lifecycle artifacts, not dismissals. Shared by the root Menu's
 * onClose guard and the Submenu close veto.
 */
export const isFocusLeavingMenuTree = (event?: Event): boolean => {
  const related = event && 'relatedTarget' in event ? (event as FocusEvent).relatedTarget : null
  return related instanceof Element && related.closest('[role="menu"]') === null
}
